import { useEffect, useState } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_month: number;
  price_year: number;
  stripe_price_id_month?: string;
  stripe_price_id_year?: string;
}

const Cenik = () => {
  const { user } = useAuth();
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const { data: packsData, error: packsError } = await supabase
          .from('credit_packs')
          .select('*')
          .order('credits');
        
        if (packsError) {
          console.error('Error fetching credit packs:', packsError);
        } else {
          setPacks((packsData || []).map((item: any) => ({
            id: item.id,
            name: item.name,
            credits: item.credits,
            price_month: item.price_month ?? 0,
            price_year: item.price_year ?? 0,
            stripe_price_id_month: item.stripe_price_id_month ?? '',
            stripe_price_id_year: item.stripe_price_id_year ?? '',
          })));
        }
      } catch (error) {
        console.error('Error in fetchPacks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <div className="text-center">Načítání...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-gray-50 to-white py-20">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Ceník
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Vyberte si plán, který vám vyhovuje.
              </p>
              
              {!user && (
                <div className="bg-[#14A48D] text-white p-6 rounded-lg mb-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Star className="h-5 w-5" />
                    <span className="font-semibold text-lg">Speciální nabídka pro nové uživatele</span>
                  </div>
                  <p className="text-lg">Zaregistrujte se a získejte 3 kredity zdarma na vyzkoušení našeho AI stagingu!</p>
                  <Link to="/register" className="inline-block mt-4">
                    <Button size="lg" className="bg-white text-[#14A48D] hover:bg-gray-100 font-bold">
                      Registrovat se zdarma
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20">
          <div className="container px-4 mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Vyberte si svůj tarif</h2>
              <p className="text-gray-600">Všechny tarify zahrnují měsíční obnovení kreditů</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {packs.map((pkg, index) => (
                <div
                  key={pkg.id}
                  className={`relative border rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow ${
                    index === 1 ? 'border-[#14A48D] ring-2 ring-[#14A48D] ring-opacity-20' : 'border-gray-200'
                  }`}
                >
                  {index === 1 && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[#14A48D] text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Nejoblíbenější
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                    <div className="text-4xl font-bold text-[#14A48D] mb-2">
                      {pkg.credits}
                    </div>
                    <p className="text-gray-600">kreditů měsíčně</p>
                  </div>

                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-gray-900">
                      {pkg.price_month} Kč
                    </div>
                    <div className="text-gray-500">měsíčně</div>
                    <div className="text-sm text-gray-400 mt-1">
                      {(pkg.price_month / pkg.credits).toFixed(0)} Kč / kredit
                    </div>
                  </div>

                  <div className="space-y-3 mb-8">
                    <div className="flex items-center">
                      <Check className="h-5 w-5 text-[#14A48D] mr-3" />
                      <span>Virtuální staging místností</span>
                    </div>
                    <div className="flex items-center">
                      <Check className="h-5 w-5 text-[#14A48D] mr-3" />
                      <span>Automatické odstranění nábytku</span>
                    </div>
                    <div className="flex items-center">
                      <Check className="h-5 w-5 text-[#14A48D] mr-3" />
                      <span>Výběr z 6 stylů interiéru</span>
                    </div>
                    <div className="flex items-center">
                      <Check className="h-5 w-5 text-[#14A48D] mr-3" />
                      <span>Výsledky do 15 minut</span>
                    </div>
                    <div className="flex items-center">
                      <Check className="h-5 w-5 text-[#14A48D] mr-3" />
                      <span>Neomezené úpravy</span>
                    </div>
                  </div>

                                     {user ? (
                     <Link to="/buy-credits">
                       <Button 
                         className={`w-full ${
                           index === 1 
                             ? 'bg-[#14A48D] hover:bg-[#11997C] text-white' 
                             : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                         }`}
                       >
                         Vybrat tarif
                       </Button>
                     </Link>
                   ) : (
                     <Link to="/register">
                       <Button 
                         className={`w-full ${
                           index === 1 
                             ? 'bg-[#14A48D] hover:bg-[#11997C] text-white' 
                             : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                         }`}
                       >
                         Začít zdarma
                       </Button>
                     </Link>
                   )}
                </div>
              ))}
            </div>

            {/* Additional Info */}
            <div className="mt-16 text-center">
              <div className="bg-gray-50 rounded-2xl p-8 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4">Jak fungují kredity?</h3>
                <div className="grid md:grid-cols-3 gap-6 text-left">
                  <div className="text-center">
                    <div className="bg-[#14A48D] text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-3">1</div>
                    <h4 className="font-semibold mb-2">Měsíční obnovení</h4>
                    <p className="text-gray-600">Každý měsíc získáte novou dávku kreditů podle zvoleného tarifu.</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-[#14A48D] text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-3">2</div>
                    <h4 className="font-semibold mb-2">Jeden kredit = jeden staging</h4>
                    <p className="text-gray-600">Každý kredit vám umožní vytvořit jeden virtuální staging místnosti.</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-[#14A48D] text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-3">3</div>
                    <h4 className="font-semibold mb-2">Bez závazků</h4>
                    <p className="text-gray-600">Můžete kdykoliv změnit nebo zrušit předplatné bez poplatků.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Cenik; 